/**
 * One notice, as an employee sees it.
 *
 * The body goes through the shared `Markdown` renderer, which emits React
 * elements and never touches `dangerouslySetInnerHTML` — so an admin (or an
 * Ivanti alert) cannot put script or markup on the home page of every employee
 * in the group. That is the whole reason the body is Markdown rather than HTML;
 * see the header of `components/common/markdown.tsx`.
 *
 * The kind is carried twice on purpose: as a coloured left accent, which is
 * scannable but invisible to a screen reader and to anyone who cannot separate
 * the hues, and as a labelled badge, which is neither.
 *
 * **A card is a headline, not the notice.** The first cut rendered every body
 * inline and put the whole seven-tip help guide on the landing page, below the
 * question box — the opposite of what a front door is for. A notice with
 * anything to read now links to its own page, the same way a ticket row links
 * to its ticket. A notice with nothing more to say — most alerts, which are one
 * sentence — is not a link, because there is nowhere for it to go.
 */

import { ChevronRight, Icon } from "@norican/icons";
import { Badge, Card, cn, Typography } from "@norican/react";
import { Link } from "react-router";

import { RelativeTime } from "@/components/common";
import { useT } from "@/i18n";
import type { Message } from "@/lib/api";

import { builtinContent } from "./builtin-content";
import { MESSAGE_KIND_ICON, MESSAGE_KIND_TONE, useMessageKindLabel } from "./message-kinds";

export interface MessageCardProps {
  message: Message;
  /**
   * Tighter padding and no timestamp, for the home page panel where the notice
   * sits in a narrow rail beside the conversation rather than on a page of its
   * own.
   */
  compact?: boolean;
  className?: string;
}

export function MessageCard({ message, compact = false, className }: MessageCardProps) {
  const { t } = useT("messages");
  const { t: ta } = useT("assistant");
  const kindLabel = useMessageKindLabel();
  const tone = MESSAGE_KIND_TONE[message.kind];

  /* A BUILTIN notice keeps its words in the catalogues rather than in its row,
     so it reads in the employee's own language instead of whichever one it was
     seeded in. Everything else — placement, rank, the badge — comes from the
     row exactly as it does for any other notice. */
  const builtin = builtinContent(message.external_id, ta);
  const title = builtin?.title ?? message.title;
  // Every optional field in the generated shape is optional because the API
  // omits its default, not because it is meaningful — an absent body is an
  // empty one.
  const body = builtin?.body ?? message.body_markdown ?? "";

  /* "Is there more to read?" — computed after the built-in text is resolved,
     because a built-in's row body is only an English fallback while its real
     content comes from the catalogue. */
  const hasMore = body.trim() !== "";

  const heading = (
    <div className="flex w-full min-w-0 items-center gap-2">
      <Icon
        icon={MESSAGE_KIND_ICON[message.kind]}
        size="sm"
        {...(tone === "neutral" ? {} : { tone })}
      />
      <Typography
        as="h3"
        variant={compact ? "body-sm" : "body"}
        className="min-w-0 flex-1 font-medium"
      >
        {title}
      </Typography>
      <Badge tone={tone} variant="subtle" size="sm">
        {kindLabel(message.kind)}
      </Badge>
      {hasMore ? <Icon icon={ChevronRight} size="sm" className="text-fg-subtle shrink-0" /> : null}
    </div>
  );

  return (
    <Card
      variant="outline"
      padding={compact ? "sm" : "md"}
      accent="left"
      accentTone={tone}
      className={cn(
        "flex w-full flex-col gap-1",
        hasMore && "ease-standard transition hover:shadow-md",
        className,
      )}
    >
      {hasMore ? (
        <Link to={`/messages/${message.message_id}`} className="flex flex-col gap-1 rounded-md">
          {heading}
        </Link>
      ) : (
        heading
      )}

      {compact || message.published_at == null ? null : (
        <Typography variant="caption" tone="muted">
          {t("publishedLabel")} <RelativeTime value={message.published_at} />
        </Typography>
      )}
    </Card>
  );
}
